<template>
  <div>
    <div class="container-fluid pb-1">
      <!-- top -->
      <div class="px-3 py-5">
        <h4 class="h3 text-muted font-weight-bold">Data Records</h4>
        <span> Fill the parameters to find/sort specific Data </span>
        <div class="row align-items-center mt-4">
          <div class="col-md-6">
            <label class="h6 font-weight-bold d-block">Search</label>
            <b-form-input
              v-model="search"
              title="Press Enter to search ..."
              :placeholder="ps"
              type="search"
              @keyup.enter="performSearch"
            ></b-form-input>
          </div>
          <div class="col-md-3">
            <label class="h6 font-weight-bold d-block">File Type</label>
            <div class="form-group mb-0">
              <b-form-select
                v-model="selectedFileType"
                class="form-control"
                :options="fileTypeOptions"
              ></b-form-select>
            </div>
          </div>
          <div class="col-md-3">
            <label class="h6 font-weight-bold d-block">Status</label>
            <div class="form-group mb-0">
              <b-form-select
                v-model="selectedStatus"
                class="form-control"
                :options="statusOptions"
              ></b-form-select>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-sm-4 my-4 row mx-1" style="top: 75px">
        <div class="col-lg-12">
          <div v-loading="tableLoading" class="border">
            <TableComponent
              :fields="fields"
              bordered
              :data="dataRecords"
              :loading="tableLoading"
              :empty-text="emptyText"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              @sort:by="(by) => (sortBy = by)"
              @sort:direction="(isDescending) => (sortDesc = isDescending)"
              @sizeChange="handlePageSizeChange"
              @selected="handleSelection"
            >
              <template #cell="{ row }">
                <span v-if="row.field.key === 'Name'">
                  <svg-icon icon-class="excel" />
                  <span style="padding-left: 5px">{{ row.value }}</span>
                </span>
                <span v-else>{{ row.value }}</span>
              </template>
            </TableComponent>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import TableComponent from "../../components/Table.vue";
import { random } from "../../utils";
import DataMixin from "./mixin";
import { DateTime } from "luxon";
import lunar from "lunr";

const formatter = (value) => {
  return new DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT);
};
/* eslint-disable */

const searchPlaceHolders = [
  "level:National",
  "level:Sub-national",
  "location:Imo",
  "target:8*",
  "target:80",
  "value:89",
  "target:7*",
  "level:National location:Imo target:8*",
  "level:National target:8*",
];

const searchFields = ["indicator", "target", "value", "location", "level"];

const normalizeDate = function (builder) {
  var pipelineFunction = function (token) {
    return token;
  };
  // Register the pipeline function so the index can be serialized
  lunar.Pipeline.registerFunction(pipelineFunction, "normalizeDate");
  // Add the pipeline function to both the indexing pipeline and the
  // searching pipeline
  builder.pipeline.before(lunar.stemmer, pipelineFunction);
  builder.searchPipeline.before(lunar.stemmer, pipelineFunction);
};

const percentageFormatter = (value) => `${value}%`;

export default {
  name: "DataRecords",
  components: {
    TableComponent,
  },
  mixins: [DataMixin],

  data() {
    return {
      filtersFrom: "preview",
      sortableFields: [
        { name: "indicator", label: "Select Indicator" },
        { name: "target", label: "Select Target" },
        { name: "location", label: "Select Location" },
        { name: "level", label: "Select Level" },
      ],
      sortBy: "created_at",
      sortDesc: true,
      fetchInterval: undefined,
      search: undefined,
      ps: searchPlaceHolders[random(0, searchPlaceHolders.length - 1)],
      selectedFileType: null,
      selectedStatus: null,
      datat: [
        {
          $id: 1,
          Name: "Document 1",
          "File Type": "PDF",
          Status: "Approved",
          "Date Created": "2023-09-19",
          Owner: "John Doe",
          // Add more data properties as needed
        },
        {
          $id: 2,
          Name: "Document 2",
          "File Type": "CSV",
          Status: "Pending",
          "Date Created": "2023-09-20",
          Owner: "Jane Smith",
          // Add more data properties as needed
        },
        // Add more sample data objects as needed
      ],
    };
  },
  computed: {
    ...mapGetters({ records: "data/records" }),
    fileTypeOptions() {
      // Define options for file types (replace with actual options)
      return [
        { value: null, text: "All" },
        { value: "pdf", text: "PDF", default: true },
        { value: "csv", text: "CSV" },
        // Add more file types as needed
      ];
    },
    statusOptions() {
      // Define options for status (replace with actual options)
      return [
        { value: null, text: "All" },
        { value: "pending", text: "Pending" },
        { value: "completed", text: "Completed" },
        // Add more status options as needed
      ];
    },
    fields() {
      // return [
      //   { key: "indicator", sortable: true },
      //   { key: "level", sortable: true },
      //   { key: "location", sortable: true, label: "Country/State" },
      //   {
      //     key: "target",
      //     label: "National Target",
      //     sortable: true,
      //     formatter: percentageFormatter,
      //   },
      //   { key: "value", sortable: true, formatter: percentageFormatter },
      // ];
      return [
        { key: "Name", sortable: true },
        { key: "File Type", sortable: true },
        { key: "Status", sortable: true },
        { key: "Date Created", sortable: true },
        { key: "Frequency", sortable: true },
        // { key: "Owner", sortable: true },
      ];
    },
    dataRecords() {
      return this.records.map(record => ({
        $id: record.id,
        Name: record.name,
        "File Type": record.file_type,
        Status: record.approved ? "Approved" : "Not Approved",
        "Date Created": record.created_at?.slice(0,10),
        Frequency: record.frequency || 'Unspecified',
        // Owner: record.owner || 'Unspecified',
        // Add more data properties as needed
      }))
    },
    preview() {
      if (this.search && window.recordsSearchIndex) {
        try {
          const results = window.recordsSearchIndex.search(this.search);
          const records = results.map((x) => {
            return this.records.find((y) => y.id == parseInt(x.ref, 10));
          });
          return records;
        } catch (error) {
          console.log(error);
        }
      }
      return this.records;
    },
    tableDescription() {
      const amount =
        this.perPageSize > this.preview.length ? this.preview.length : this.perPageSize;
      return `Showing ${amount} of ${this.preview.length} records`;
    },
  },
  created() {
    // if (!this.records.length) {
    this.loadRecords();
    // }
    this.buildSearchIndex(JSON.parse(JSON.stringify(this.records)));
  },
  beforeDestroy() {
    if (this.fetchInterval) clearInterval(this.fetchInterval);
  },
  mounted() {
    this.fetchInterval = setInterval(() => {
      this.loadRecords();
    }, 600000);
  },
  methods: {
    /**
     *
     */
    performSearch() {},
    /**
     *
     */
    loadRecords() {
      if (this.tableLoading) return;
      this.tableLoading = true;
      this.$store
        .dispatch("data/fetchRecords")
        .catch((error) => {
          const statusCode = error?.response?.status || 500;
          if (403 == statusCode) {
            // @dev render test data
            this.$store.commit("data/SET_RECORDS", this.generateTestData());
            this.$notify({
              position: "top-right",
              message: "Error fetching records: StatusCode::403. Using test data",
              duration: 0,
              type: "warning",
            });
          }
        })
        .finally(() => {
          this.tableLoading = false;
          this.buildSearchIndex(JSON.parse(JSON.stringify(this.records)));
        });
    },
    /**
     * High performant search algorithm, performs a local search on records
     * present in the store
     *
     * @see https://lunrjs.com/
     */
    async buildSearchIndex(records) {
      records = records.map((x) => {
        x.created_at = formatter(x.created_at);
        return x;
      });

      const index = lunar(function () {
        this.use(normalizeDate);
        searchFields.forEach(function (field) {
          this.field(field);
        }, this);

        this.ref("id");

        records.forEach(function (doc) {
          this.add(doc);
        }, this);
      });

      // Store on window to avoid memory leak on large datasets
      window.recordsSearchIndex = index;
    },

    // TODO fix error while loading lists
    generateTestData() {
      return new Array(50).fill(0).map((_, index) => {
        let x = {};
        x.id = index;

        x.indicator = "Skilled Birth Attendance";
        x.level = ["Sub-national", "National"][random(0, 2)];
        x.location = ["Nigeria", "Rivers", "Yobe", "Taraba"][random(0, 3)];
        x.target = random(0, 100);
        x.value = random(0, 80);
        return x;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
input::placeholder {
  color: #dae2e9;
  opacity: 1;
}
input:-ms-input-placeholder {
  color: #dae2e9;
  opacity: 1;
}

input::-ms-input-placeholder {
  color: #dae2e9;
  opacity: 1;
}
</style>
